import { z } from 'zod';
import { amountCents, email, isoDate, uuid } from './common.js';

/** Team name typed by the captain at checkout (P-20). */
export const teamName = z.string().trim().min(2).max(60);

/** Join code of an invitation link (P-21): URL-safe, 22 characters. */
export const joinCode = z.string().trim().min(16).max(64);

/**
 * POST /me/teams/:id/members — teammate emails, raw strings like invitations: each address gets
 * its own verdict instead of failing the batch. The seats left bound how many are accepted.
 */
export const addTeamMembersInput = z.object({
  emails: z.array(z.string().trim().max(254)).min(1).max(20),
});
export type AddTeamMembersInput = z.infer<typeof addTeamMembersInput>;

export const teamMemberResultStatus = z.enum([
  'sent',
  'invalid',
  'self', // the captain's own email
  'duplicate', // repeated in the same batch
  'already_member', // already invited to or paid on this team
  'already_registered', // holds a paid registration for this format
  'team_full', // no seat left (team_size_max)
]);
export type TeamMemberResultStatus = z.infer<typeof teamMemberResultStatus>;

export const addTeamMembersOutput = z.object({
  /** One entry per submitted email, in the submitted order. */
  results: z.array(z.object({ email: z.string(), status: teamMemberResultStatus })),
});
export type AddTeamMembersOutput = z.infer<typeof addTeamMembersOutput>;

export const teamStatus = z.enum(['forming', 'complete', 'cancelled']);
export type TeamStatus = z.infer<typeof teamStatus>;
export const teamMemberStatus = z.enum([
  'invited',
  'joined',
  'replaced',
  'refunded',
  'superseded',
  'moved',
  'cancelled',
]);
export type TeamMemberStatus = z.infer<typeof teamMemberStatus>;

/** A seat on the roster (P-20): a pending invitation or a paid member; dead seats are not listed. */
export const teamRosterMember = z.object({
  id: uuid,
  /** Email the captain typed (invited) or the member's own email once paid; masked for members. */
  email: z.string(),
  status: z.enum(['invited', 'joined']),
  isCaptain: z.boolean(),
  /** Member's name once known from their profile. */
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  /** Documents complete for this member (joined only). */
  documentsComplete: z.boolean(),
  invitedAt: isoDate,
  joinedAt: isoDate.nullable(),
  /**
   * Join link of an invited seat, so the captain can pass it on themselves (the member gets the
   * same link by email). Null once paid, and for members who are not the captain.
   */
  joinUrl: z.string().url().nullable(),
});
export type TeamRosterMember = z.infer<typeof teamRosterMember>;

export const teamSummary = z.object({
  id: uuid,
  name: z.string(),
  status: teamStatus,
  formatId: uuid,
  formatName: z.string(),
  /** Null = show `formatName` (French) in English too (A-01). */
  formatNameEn: z.string().nullable(),
  formatStartsAt: isoDate.nullable(),
  sizeMin: z.number().int().positive(),
  /** Null = unlimited (record 0021). */
  sizeMax: z.number().int().positive().nullable(),
  /** Whether the current user is this team's captain (a per-team capability, not a role). */
  isCaptain: z.boolean(),
  members: z.array(teamRosterMember),
});
export type TeamSummary = z.infer<typeof teamSummary>;

/** GET /me/teams — teams the participant captains or paid a place in, for the profile. */
export const myTeams = z.object({ teams: z.array(teamSummary) });
export type MyTeams = z.infer<typeof myTeams>;

/**
 * GET /teams/join/:code — what an invitation link opens (P-21, record 0021 D13). 410
 * `link_invalid` for an unknown or dead code (replaced, refunded, superseded). `status` says
 * whether the member can pay their place now:
 * - `invited`: open the checkout with the format pre-selected at the team's locked tier;
 * - `joined`: this seat is already paid;
 * - `cancelled`: the organiser cancelled the team;
 * - `full`: `team_size_max` paid members already;
 * - `sold_out`: the format has no place left;
 * - `not_on_sale`: the format's sales are closed (no tier open).
 */
export const joinTeamView = z.object({
  status: z.enum(['invited', 'joined', 'cancelled', 'full', 'sold_out', 'not_on_sale']),
  event: z.object({ id: uuid, name: z.string(), edition: z.string() }),
  format: z.object({
    id: uuid,
    name: z.string(),
    /** Null = show `name` (French) in English too. */
    nameEn: z.string().nullable(),
    startsAt: isoDate.nullable(),
    requiresMedicalDocument: z.boolean(),
    /** The place's price for this member: the team's locked tier (record 0021 D1). */
    priceCents: amountCents,
    tierName: z.string(),
  }),
  team: z.object({
    id: uuid,
    name: z.string(),
    sizeMin: z.number().int().positive(),
    sizeMax: z.number().int().positive().nullable(),
    /** Paid members so far, the captain included. */
    joined: z.number().int().nonnegative(),
  }),
  /**
   * The captain, by first name and last initial when known, else by email (decision 0009). Null
   * when a refund left the team without one: the place can still be paid, the back office
   * appoints a captain (record 0021, reviewed 2026-10-28).
   */
  captain: z
    .object({
      firstName: z.string().nullable(),
      lastInitial: z.string().nullable(),
      email,
    })
    .nullable(),
  /** Email the captain typed: pre-filled on the checkout, the member may pick another (P-21). */
  invitedEmail: email,
});
export type JoinTeamView = z.infer<typeof joinTeamView>;
